import React from 'react'
import { Typography, makeStyles } from '@material-ui/core'
import Card from '@material-ui/core/Card'
import Zoom from '@mui/material/Zoom'
import StarIcon from '@material-ui/icons/Star'
import { useIntl } from 'react-intl'

import DefaultCourseGroupImage from '../../../assets/images/courseGroup/FallbackCourseGroupImage.jpeg'
import { CapeeshColors } from '../../../assets/ColorPalette'
import type { CourseMetadataV1Dto, CourseProgressionDto } from '../../../Logic/StudentCourseDtos'
import { useStudentImage } from '../useStudentImage'
import { studentScreenMessages } from '../../../Views/StudentPrivate/StudentScreen.i18n.Messages'

// ─── Styles ───────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    cardRoot: {
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 300ms ease-in-out, transform 200ms ease',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.06)',
        '&:hover': {
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-1px)',
        },
        display: 'flex',
        flexDirection: 'column' as const,
        backgroundColor: '#FFFFFF',
    },
    imageContainer: {
        position: 'relative' as const,
        width: '100%',
        height: 160,
        overflow: 'hidden',
        backgroundColor: CapeeshColors.Gray5,
    },
    topicImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
    },
    starBadge: {
        position: 'absolute' as const,
        top: 12,
        right: 12,
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        borderRadius: 20,
        padding: '4px 10px',
        boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.12)',
    },
    starText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
    },
    starIcon: {
        fontSize: 16,
        color: CapeeshColors.YellowBright,
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column' as const,
        padding: '14px 16px 0 16px',
        flexGrow: 1,
    },
    topicName: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        lineHeight: '140%',
        color: CapeeshColors.DarkText,
        marginBottom: 4,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    topicDescription: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        lineHeight: '150%',
        color: CapeeshColors.Gray1,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flexGrow: 1,
    },
    progressionContainer: {
        padding: '12px 16px 14px 16px',
    },
    progressionRow: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: 8,
    },
    progressionBarBackground: {
        flex: 1,
        height: 6,
        borderRadius: 3,
        backgroundColor: CapeeshColors.Gray5,
        overflow: 'hidden',
    },
    progressionBarFill: {
        height: '100%',
        borderRadius: 3,
        transition: 'width 600ms ease-in-out',
    },
    progressionLabel: {
        fontFamily: 'Rubik',
        fontSize: 11,
        fontWeight: 500,
        color: CapeeshColors.Gray1,
        minWidth: 32,
        textAlign: 'right' as const,
    },
}))

// ─── Types ────────────────────────────────────────────────────────

interface TopicCardProps {
    topic: CourseMetadataV1Dto
    progression: CourseProgressionDto | null
    onSelect?: (topicId: number) => void
    index: number
}

// ─── Component ────────────────────────────────────────────────────

const TopicCard: React.FC<TopicCardProps> = ({
    topic,
    progression,
    onSelect,
    index,
}) => {
    const classes = useStyles()
    const intl = useIntl()
    const imageBlobUrl = useStudentImage(topic?.imageUrl)

    if (!topic) {
        return null
    }

    const name = topic.l2TopicName || topic.l1TopicName || intl.formatMessage(studentScreenMessages.topicFallback)
    const description = topic.l2TopicDescription || topic.l1TopicDescription || ''
    const starsAttained = progression?.starsAttained ?? 0
    const starsTotal = progression?.starsAttainableInCourse ?? 0
    const normalizedProgression = progression?.courseNormalizedProgression ?? 0
    const progressPercent = Math.min(Math.round(normalizedProgression * 100), 100)
    const isComplete = normalizedProgression >= 0.999

    const handleClick = () => {
        if (onSelect) {
            onSelect(topic.topicId)
        }
    }

    return (
        <Zoom in={true} style={{ transitionDelay: `${(index || 0) * 50}ms` }}>
            <Card className={classes.cardRoot} onClick={handleClick}>
                <div className={classes.imageContainer}>
                    <img
                        src={imageBlobUrl || DefaultCourseGroupImage}
                        className={classes.topicImage}
                        alt={name}
                    />
                    <div className={classes.starBadge}>
                        <Typography className={classes.starText}>
                            {starsAttained}/{starsTotal}
                        </Typography>
                        <StarIcon className={classes.starIcon} />
                    </div>
                </div>

                <div className={classes.cardBody}>
                    <Typography className={classes.topicName}>
                        {name}
                    </Typography>
                    {description && (
                        <Typography className={classes.topicDescription}>
                            {description}
                        </Typography>
                    )}
                </div>

                <div className={classes.progressionContainer}>
                    <div className={classes.progressionRow}>
                        <div className={classes.progressionBarBackground}>
                            <div
                                className={classes.progressionBarFill}
                                style={{
                                    width: `${progressPercent}%`,
                                    backgroundColor: isComplete
                                        ? CapeeshColors.GreenBright
                                        : CapeeshColors.Primary600,
                                }}
                            />
                        </div>
                        <Typography className={classes.progressionLabel}>
                            {progressPercent}%
                        </Typography>
                    </div>
                </div>
            </Card>
        </Zoom>
    )
}

export default TopicCard
